/*
 * Column definitions for the Preview grid — the IM Item Master rows the Record Range selects.
 *
 * WHICH COLUMNS. qryIMItem declares 49 persistent fields and every one of them is visible, so
 * TAbWhereBuilder.Preview dumps all 49 into the desktop grid — 40 of which are the numbered
 * user-defined blocks. The nine business columns below are the ones AI8 opens on, in the dataset's own
 * FieldList order (Customer, Item, Description - 1, Description - 2, Class, …), which is exactly what a
 * user sees before scrolling. The user-defined blocks are deliberately not projected: 40 columns of
 * USERDEFINEDQUANTITY_n is a column chooser to fight, not a preview. Filtering is unaffected — the
 * Record Range catalogue still offers all 49 fields.
 *
 * Record # is our addition (Architecture.md rule 10) and is lifted to the front, as every migrated grid
 * does; KEYSEQUENCE is not a persistent field in the dataset, so AI8 had no such column.
 *
 * dataField casing matches ItemMasterRowDTO's property names exactly, because the endpoint serialises
 * with DefaultContractResolver, i.e. PascalCase straight off the DTO.
 *
 * dataType is declared on EVERY column, deliberately. DevExtreme only infers it from the first non-null
 * value, and Class, Sub Class and all three dates are routinely empty for a whole result set. With
 * nothing to infer from, the column gets no filter-row editor and the user silently loses the ability to
 * filter it.
 *
 * minWidth on every column keeps `columnAutoWidth` from squeezing the columns: once the total exceeds
 * the container DevExtreme shows a horizontal scrollbar instead of compressing everything to slivers.
 *
 * The array is module-level and frozen because OperationResultGrid is memo-wrapped: a fresh array per
 * render would remount GridLayoutManager and re-fire GetGridLayouts on every keystroke.
 */

const TEXT = "string";
const DATE = "date";

// Widths tuned to the caption plus a plausible value, so nothing truncates at the default zoom.
const W_SHORT = 90;
const W_CODE = 140;
const W_NAME = 240;

export const PREVIEW_KEY_EXPR = "RowId";

const column = (dataField, caption, dataType, width) => ({
    dataField,
    caption,
    dataType,
    minWidth: width,
});

const text = (dataField, caption, width = W_CODE) => column(dataField, caption, TEXT, width);
const date = (dataField, caption) => column(dataField, caption, DATE, W_CODE);

/*
 * The key. Hidden and kept out of the column chooser (Architecture.md rule 8) — users never see raw ids,
 * and AI8 did not show it either: ROWID is not a persistent field in the dataset.
 */
const rowIdColumn = { dataField: "RowId", dataType: TEXT, visible: false, showInColumnChooser: false };

// Captions verbatim from the DFM DisplayLabels, including the spaced-hyphen "Description - 1".
export const PREVIEW_COLUMNS = Object.freeze([
    rowIdColumn,
    text("RecordNumber", "Record #", W_SHORT),
    text("IdentityId", "Customer"),
    text("ItemCode", "Item"),
    text("Description1", "Description - 1", W_NAME),
    text("Description2", "Description - 2", W_NAME),
    text("ClassCode", "Class"),
    text("SubClassCode", "Sub Class"),
    date("DateIntroduced", "Date Introduced"),
    date("DateAvailable", "Date Available"),
    date("DateExpiration", "Date Expiration"),
]);
